import * as React from "react";
import {useParams} from "react-router";
import {Link as RouterLink} from "react-router-dom";
import {
    Alert,
    Box,
    Button,
    FormControl,
    FormGroup,
    Link,
    MenuItem,
    Paper,
    Select,
    SelectChangeEvent,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableSortLabel,
} from "@mui/material";
import TextField from "@mui/material/TextField";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import {toast} from "react-toastify";
import Loading from "../../components/loading";
import TableHeaderRow from "../../components/TableHeaderRow";
import StripedTableRow from "../../components/StripedTableRow";
import {campusAdd, campusSetActive, campusUpdate, getProgramSet, programSetImport} from "./programSetService";
import {searchProgramSets} from "./programSetsService";
import ProgramSetZipsModal from "./programSetZipsModal";
import ProgramSetStatesModal from "./programSetStatesModal";
import ProgramSetCampusDelete from "./programSetCampusDelete";

const oldUrl = process.env.REACT_APP_OLD_URL;
const warningCell = {backgroundColor: '#ffcdd2'};
const nowrapCell = {whiteSpace: 'nowrap'};
const denseTable = {'& .MuiTableCell-root': {padding: {xs: '4px 6px', sm: '6px 16px'}}};
const denseInput = {'& .MuiInputBase-input': {padding: '5px 8px', fontSize: '13px'}};

// Data editors get their own hues so they read apart from the row actions.
const softButton = (hue: string, tint: string) => ({
    color: hue,
    borderColor: hue,
    '&:hover': {borderColor: hue, backgroundColor: tint},
});
const zipsButton = softButton('#00796b', 'rgba(0, 121, 107, 0.08)');
const statesButton = softButton('#6a1b9a', 'rgba(106, 27, 154, 0.08)');
const programsButton = softButton('#e65100', 'rgba(230, 81, 0, 0.08)');

export default function ProgramSetPage() {
    const {id} = useParams();
    const programSetId = parseInt(id || "0", 10);

    const [loading, setLoading] = React.useState(true);
    const [programSet, setProgramSet] = React.useState(null as any);
    const [sort, setSort] = React.useState({column: 'active', isAsc: true});
    const [newCampus, setNewCampus] = React.useState('');
    const [edits, setEdits] = React.useState({} as any);
    const [importSets, setImportSets] = React.useState([] as any[]);
    const [importId, setImportId] = React.useState('');
    const [zipsCampus, setZipsCampus] = React.useState(null as any);
    const [statesCampus, setStatesCampus] = React.useState(null as any);
    const [deleteCampus, setDeleteCampus] = React.useState(null as any);

    React.useEffect(() => {
        document.title = "Program Set Update | DN";
    }, []);

    const reload = async (sSort: {column: string, isAsc: boolean}) => {
        setLoading(true);
        const ps = await getProgramSet(programSetId, sSort.column, sSort.isAsc);
        setProgramSet(ps);

        const map: any = {};
        if (ps) {
            ps.campuses.forEach((c: any) => {
                map[c.name] = {name: c.name, displayName: c.displayName};
            });
        }
        setEdits(map);

        if (ps && ps.campuses.length === 0) {
            const list = await searchProgramSets(1000, '');
            setImportSets(list.programSets.filter((p: any) => p.id !== programSetId));
        }

        setLoading(false);
    }

    React.useEffect(() => {
        reload(sort).then(() => {});
    }, [programSetId]);

    const doSort = async (column: string) => {
        const s = sort.column === column
            ? {column, isAsc: !sort.isAsc}
            : {column, isAsc: true};
        setSort(s);
        await reload(s);
    }

    const handleAdd = async () => {
        if (!newCampus.trim()) {
            toast.error('Please enter a Campus Code');
            return;
        }

        if (await campusAdd(programSetId, newCampus)) {
            toast.success('Campus added successfully');
            setNewCampus('');
            await reload(sort);
        }
    }

    const handleUpdate = async (campus: any) => {
        const edit = edits[campus.name] || {};
        if (await campusUpdate(programSetId, campus.name, edit.name, edit.displayName)) {
            toast.success('Campus updated successfully');
            await reload(sort);
        }
    }

    const handleSetActive = async (campus: any, active: boolean) => {
        if (await campusSetActive(programSetId, campus.name, active)) {
            toast.success(active ? 'Campus restored' : 'Campus deleted');
            await reload(sort);
        }
    }

    const handleImport = async () => {
        const sourceId = parseInt(importId, 10);
        if (!sourceId) {
            toast.error('Please select a Program Set to import');
            return;
        }

        if (await programSetImport(programSetId, sourceId)) {
            toast.success('Program Set imported successfully');
            setImportId('');
            await reload(sort);
        }
    }

    const setEdit = (name: string, field: string, value: string) => {
        setEdits({...edits, [name]: {...edits[name], [field]: value}});
    }

    if (loading) return <Loading/>;
    if (!programSet) return <Alert severity="warning">Program Set not found</Alert>;

    return (
        <>
            <h2>Program Set Update</h2>
            <Box sx={{margin: '0 0 10px 0', fontSize: '13px'}}>
                <b>{programSet.name}</b> [{programSet.id}]
                <span> &mdash; </span>
                <Link component={RouterLink} to="/programSets">Back to Program Sets</Link>
            </Box>

            <FormGroup row={true}>
                <FormControl sx={{m: 1, minWidth: {xs: 160, sm: 240}}} size="small">
                    <TextField
                        autoComplete={'off'}
                        id="campusCode"
                        label="Campus Code"
                        size={'small'}
                        value={newCampus}
                        inputProps={{maxLength: 100}}
                        onChange={(e) => setNewCampus(e.target.value)}
                    />
                </FormControl>
                <FormControl sx={{m: 1, minWidth: 120}} size="small">
                    <Button aria-label="Add" onClick={handleAdd} variant={'contained'} color={'warning'}>
                        <AddBoxOutlinedIcon />&nbsp;Add Campus
                    </Button>
                </FormControl>
            </FormGroup>

            {programSet.campuses.length === 0 ?
                <>
                    <Alert severity="warning" sx={{margin: '10px 0'}}>No Campuses</Alert>
                    <FormGroup row={true}>
                        <FormControl sx={{m: 1, minWidth: 320}} size="small">
                            <Select
                                value={importId}
                                displayEmpty
                                size={'small'}
                                onChange={(e: SelectChangeEvent) => setImportId(e.target.value)}
                            >
                                <MenuItem value=""><em>Import Program Set</em></MenuItem>
                                {importSets.map((p: any) => (
                                    <MenuItem key={p.id} value={p.id.toString()}>{p.name} - {p.id}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl sx={{m: 1, minWidth: 180}} size="small">
                            <TextField
                                autoComplete={'off'}
                                id="importId"
                                label="or type Program Set ID"
                                size={'small'}
                                value={importId}
                                onChange={(e) => setImportId(e.target.value)}
                            />
                        </FormControl>
                        <FormControl sx={{m: 1, minWidth: 120}} size="small">
                            <Button aria-label="Import" onClick={handleImport} variant={'contained'}>Import</Button>
                        </FormControl>
                    </FormGroup>
                </>
                :
                <TableContainer component={Paper} sx={{maxWidth: 'calc(100vw - 10px)'}}>
                    <Table size="small" sx={denseTable}>
                        <TableHead>
                            <TableHeaderRow>
                                <TableCell sx={{width: '100%'}}>
                                    <TableSortLabel
                                        active={sort.column === 'name'}
                                        direction={sort.column === 'name' && !sort.isAsc ? 'desc' : 'asc'}
                                        onClick={() => {doSort('name')}}
                                    >
                                        Display Name
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={sort.column === 'campus'}
                                        direction={sort.column === 'campus' && !sort.isAsc ? 'desc' : 'asc'}
                                        onClick={() => {doSort('campus')}}
                                    >
                                        Campus
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>Campus Name</TableCell>
                                <TableCell>Zips</TableCell>
                                <TableCell>Programs</TableCell>
                                <TableCell>-/+ Campus</TableCell>
                            </TableHeaderRow>
                        </TableHead>
                        <TableBody>
                            {programSet.campuses.map((row: any) => (
                                <StripedTableRow key={row.name}>
                                    <TableCell sx={{width: '100%'}}>
                                        <TextField
                                            autoComplete={'off'}
                                            size={'small'}
                                            fullWidth
                                            sx={{...denseInput, minWidth: 200}}
                                            value={(edits[row.name] && edits[row.name].displayName) || ''}
                                            onChange={(e) => setEdit(row.name, 'displayName', e.target.value)}
                                        />
                                    </TableCell>
                                    <TableCell sx={nowrapCell}>
                                        <TextField
                                            autoComplete={'off'}
                                            size={'small'}
                                            sx={{...denseInput, minWidth: {xs: 110, sm: 240}}}
                                            value={(edits[row.name] && edits[row.name].name) || ''}
                                            inputProps={{maxLength: 100}}
                                            onChange={(e) => setEdit(row.name, 'name', e.target.value)}
                                        />
                                    </TableCell>
                                    <TableCell sx={nowrapCell}>
                                        <Button size={'small'} variant={'outlined'} onClick={() => handleUpdate(row)}>Update</Button>
                                    </TableCell>
                                    <TableCell sx={row.zipWarning ? {...warningCell, ...nowrapCell} : nowrapCell}>
                                        {row.active ?
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Button size={'small'} variant={'outlined'} sx={zipsButton} onClick={() => setZipsCampus(row)}>Zips</Button>
                                                <Button size={'small'} variant={'outlined'} sx={statesButton} onClick={() => setStatesCampus(row)}>States</Button>
                                                {row.currentlyOnline && <span>(Currently Online)</span>}
                                            </Stack>
                                            : <>&nbsp;</>
                                        }
                                    </TableCell>
                                    <TableCell sx={row.programWarning ? {...warningCell, ...nowrapCell} : nowrapCell}>
                                        {row.active ?
                                            <Button
                                                size={'small'}
                                                variant={'outlined'}
                                                sx={programsButton}
                                                href={`${oldUrl}/hp_program_set_program_update.php?hp_campus_search_id=${programSet.id}&hp_program_search_id=${row.programSearchId ?? ''}&name=${encodeURIComponent(row.name)}`}
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                Programs
                                            </Button>
                                            : <>&nbsp;</>
                                        }
                                    </TableCell>
                                    <TableCell sx={nowrapCell}>
                                        {row.active ?
                                            <Button size={'small'} variant={'outlined'} color={'error'} onClick={() => handleSetActive(row, false)}>Delete</Button>
                                            :
                                            <Stack direction="row" spacing={1}>
                                                <Button size={'small'} variant={'outlined'} color={'success'} onClick={() => handleSetActive(row, true)}>Undo Delete</Button>
                                                <Button size={'small'} variant={'outlined'} color={'error'} onClick={() => setDeleteCampus(row)}>Perma-Delete</Button>
                                            </Stack>
                                        }
                                    </TableCell>
                                </StripedTableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            }

            <ProgramSetZipsModal programSetId={programSet.id} campus={zipsCampus} setCampus={setZipsCampus} reload={() => {reload(sort).then(() => {})}} />
            <ProgramSetStatesModal programSetId={programSet.id} campus={statesCampus} setCampus={setStatesCampus} reload={() => {reload(sort).then(() => {})}} />
            <ProgramSetCampusDelete programSetId={programSet.id} campus={deleteCampus} setCampus={setDeleteCampus} reload={() => {reload(sort).then(() => {})}} />
        </>
    )
}
